import enabledCurrencies from '@data/enabled-currencies';
import { ChevronDown } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import styled from 'styled-components';
import BrowseMore from '@/components/browse-more';
import HowItWorks from '@/components/how-it-works';
import { FAQSchema } from '@/components/json-ld';
import SEO from '@/components/seo';
import {
  Button,
  Card,
  CtaPanel,
  Eyebrow,
  HeroBackground,
  HeroTitle,
  Lead,
  Muted,
  Section,
  SectionContainer,
  SectionHeader,
  Text,
} from '@/components/ui';
import config from '@/config';
import i18n from '@/i18n.json';
import { loadMessages } from '@/lib/messages';
import { currencyName, groupByNetwork } from '@/lib/networks';
import { up } from '@/themes';
import { shareImage } from '@/utils/share-image';

const FAQ_KEYS = ['currencies', 'timing', 'fees', 'wrongAmount', 'verification'];

const Hero = styled.section`
  isolation: isolate;
  overflow: hidden;
  padding: 64px 0;
  position: relative;

  ${up.sm} {
    padding: 96px 0;
  }
`;

const HeroLayout = styled.div`
  align-items: center;
  display: grid;
  gap: 40px;

  ${up.lg} {
    gap: 64px;
    grid-template-columns: minmax(0, 1fr) auto;
  }
`;

const Title = styled(HeroTitle)`
  margin-top: 16px;
`;

const Subtitle = styled(Lead)`
  margin-top: 24px;
  max-width: 672px;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 32px;

  ${up.sm} {
    flex-direction: row;
  }
`;

/* The network's own mark on a single tint: no per-network colours. */
const LogoPanel = styled.div`
  align-items: center;
  background-color: color-mix(in oklab, var(--primary) 8%, var(--card));
  border: 1px solid var(--border);
  border-radius: var(--radius-4xl);
  box-shadow: var(--shadow-lg);
  display: none;
  height: 240px;
  justify-content: center;
  width: 240px;

  ${up.lg} {
    display: flex;
  }

  img {
    height: 120px;
    object-fit: contain;
    width: 120px;
  }
`;

const CurrencyGrid = styled.ul`
  display: grid;
  gap: 12px;
  list-style: none;
  margin: 40px 0 0;
  padding: 0;

  ${up.sm} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  ${up.lg} {
    gap: 16px;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const CurrencyCard = styled(Card)`
  align-items: center;
  flex-direction: row;
  gap: 12px;
  height: 100%;

  img {
    border-radius: 9999px;
    flex-shrink: 0;
    height: 40px;
    width: 40px;
  }
`;

const CurrencyText = styled.div`
  min-width: 0;
`;

const CurrencyName = styled(Text)`
  font-weight: 600;
  overflow-wrap: anywhere;
`;

const FaqList = styled.div`
  display: grid;
  gap: 12px;
  margin: 40px auto 0;
  max-width: 768px;
`;

const FaqItem = styled.details`
  background-color: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  color: var(--card-foreground);

  &[open] svg {
    transform: rotate(180deg);
  }
`;

const FaqQuestion = styled.summary`
  align-items: center;
  border-radius: var(--radius-xl);
  cursor: pointer;
  display: flex;
  font-size: 16px;
  font-weight: 600;
  gap: 16px;
  justify-content: space-between;
  line-height: 24px;
  list-style: none;
  padding: 16px 20px;

  &::-webkit-details-marker {
    display: none;
  }

  &:focus-visible {
    outline: 2px solid var(--ring);
    outline-offset: 2px;
  }

  svg {
    color: var(--muted-foreground);
    flex-shrink: 0;
    transition: transform 200ms ease;
  }

  @media (prefers-reduced-motion: reduce) {
    svg {
      transition: none;
    }
  }
`;

const FaqAnswer = styled.p`
  color: var(--muted-foreground);
  font-size: 16px;
  line-height: 24px;
  margin: 0;
  padding: 0 20px 20px;
`;

const FaqFooter = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 32px;
`;

// `networkCurrencies`, not `currencies`: _app.js passes every page a `currencies` prop of its own.
const NetworkPage = ({ network, networkCurrencies, currencyList, canonicalUrl }) => {
  const t = useTranslations('network');
  const locale = useLocale();
  const name = network.name;
  const logoUrl = `${config.assetsUrl}/networks/${network.servicePath}.avif`;

  // One source for the visible FAQ and its FAQPage JSON-LD, so they cannot drift.
  const faqs = FAQ_KEYS.map((key) => ({
    key,
    question: t(`faq.${key}.question`, { network: name }),
    answer: t(`faq.${key}.answer`, { network: name, currencies: currencyList }),
  }));

  return (
    <>
      <SEO
        title={t('seoTitle', { network: name })}
        description={t('seoDescription', { network: name, count: networkCurrencies.length })}
        url={canonicalUrl}
        hreflangPath={`/networks/${network.slug}`}
        imageUrl={shareImage.network(network.slug, locale)}
        imageAlt={t('heroTitle', { network: name })}
      />
      <FAQSchema faqs={faqs} />

      <Hero>
        <HeroBackground />
        <SectionContainer>
          <HeroLayout>
            <div>
              <Eyebrow>{t('eyebrow')}</Eyebrow>
              <Title>{t('heroTitle', { network: name })}</Title>
              <Subtitle $large>{t('heroSubtitle', { network: name })}</Subtitle>
              <Actions>
                <Button $size="pill" href="/buy">
                  {t('browseGiftCards')}
                </Button>
                <Button $size="pill" $variant="outline" href="/networks">
                  {t('allNetworks')}
                </Button>
              </Actions>
            </div>
            <LogoPanel>
              {/* biome-ignore lint/performance/noImgElement: small pre-sized AVIF icon from the CDN */}
              <img src={logoUrl} alt={t('logoAlt', { network: name })} width={120} height={120} />
            </LogoPanel>
          </HeroLayout>
        </SectionContainer>
      </Hero>

      <Section id="payment-currencies" $background="var(--muted)">
        <SectionHeader
          title={t('currenciesTitle', { network: name })}
          description={t('currenciesSubtitle')}
        />
        <CurrencyGrid>
          {networkCurrencies.map((currency) => (
            <li key={currency.symbol}>
              <CurrencyCard $padding="sm">
                {/* biome-ignore lint/performance/noImgElement: small pre-sized AVIF icon from the CDN */}
                <img src={currency.image} alt="" width={40} height={40} loading="lazy" />
                <CurrencyText>
                  <CurrencyName>{currency.name}</CurrencyName>
                  <Muted>{currency.displaySymbol}</Muted>
                </CurrencyText>
              </CurrencyCard>
            </li>
          ))}
        </CurrencyGrid>
      </Section>

      <HowItWorks />

      <BrowseMore />

      <Section id="faq">
        <SectionHeader align="center" title={t('faqTitle', { network: name })} />
        <FaqList>
          {faqs.map((faq) => (
            <FaqItem key={faq.key}>
              <FaqQuestion>
                {faq.question}
                <ChevronDown size={20} strokeWidth={1.75} aria-hidden="true" />
              </FaqQuestion>
              <FaqAnswer>{faq.answer}</FaqAnswer>
            </FaqItem>
          ))}
        </FaqList>
        <FaqFooter>
          <Button $variant="outline" $size="lg" href="/contact">
            {t('faqSupport')}
          </Button>
        </FaqFooter>
      </Section>

      <Section id="browse" $spacing="compact">
        <CtaPanel
          title={t('ctaTitle', { network: name })}
          description={t('ctaSubtitle')}
          actions={[{ label: t('ctaButton'), href: '/buy' }]}
        />
      </Section>
    </>
  );
};

/** "USDC, USDT and SOL", built on the server only so the markup cannot differ on hydration. */
const listSymbols = (currencies, locale) => {
  const symbols = [...new Set(currencies.map((currency) => currency.displaySymbol))];
  return new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(symbols);
};

export async function getStaticPaths({ locales }) {
  const networks = groupByNetwork(enabledCurrencies);

  return {
    paths: locales.flatMap((locale) =>
      networks.map((network) => ({ params: { network: network.slug }, locale }))
    ),
    fallback: false,
  };
}

export async function getStaticProps({ params, locale }) {
  const network = groupByNetwork(enabledCurrencies).find(({ slug }) => slug === params.network);

  if (!network) {
    return { notFound: true };
  }

  const { currencies, ...details } = network;
  const localePrefix = locale === i18n.defaultLocale ? '' : `/${locale}`;

  return {
    props: {
      network: details,
      networkCurrencies: currencies.map((currency) => ({
        symbol: currency.symbol,
        displaySymbol: currency.displaySymbol,
        name: currencyName(currency),
        image: currency.image,
      })),
      currencyList: listSymbols(currencies, locale),
      canonicalUrl: `${config.rootUrl}${localePrefix}/networks/${network.slug}`,
      messages: loadMessages(locale, { include: ['network'] }),
    },
  };
}

export default NetworkPage;
